/**
 * Drawing pieces shared by the four Software Basics explainers.
 *
 * Everything is in the 160x90 stage's own units, and everything takes a
 * `beat` so a scene can reveal itself a line at a time rather than
 * arriving all at once. A diagram that appears complete before the
 * narrator has explained it has already given the answer away.
 */

/** A labelled box. `lit` brightens it when the narration reaches it. */
export function Box({ x, y, w, h, label, sub, lit, tone = 'grape' }) {
  const [X, Y, W, H] = [x, y, w, h].map(Number);
  return (
    <g className={`bl-part is-${tone} ${lit ? 'is-lit' : ''}`}>
      <rect x={X} y={Y} width={W} height={H} rx="2" />
      <text className="bl-part-label" x={X + W / 2} y={sub ? Y + H / 2 - 0.4 : Y + H / 2 + 1.6}>
        {label}
      </text>
      {sub && (
        <text className="bl-part-sub" x={X + W / 2} y={Y + H / 2 + 4.2}>{sub}</text>
      )}
    </g>
  );
}

/** One bit. Square, on or off, with the digit in it. */
export function Bit({ x, y, on, size = 7, label, dim }) {
  const [X, Y, S] = [x, y, size].map(Number);
  return (
    <g className={`xp-bit ${on ? 'is-on' : ''} ${dim ? 'is-dim' : ''}`}>
      <rect x={X} y={Y} width={S} height={S} rx="1.2" />
      <text x={X + S / 2} y={Y + S / 2 + S * 0.26}>{on ? '1' : '0'}</text>
      {label && <text className="xp-bit-label" x={X + S / 2} y={Y - 1.6}>{label}</text>}
    </g>
  );
}

/** A row of bits from a number. `bits` is how many places to draw. */
export function BitRow({ x, y, value, bits = 8, size = 7, gap = 1.4, labels, upto }) {
  const cells = [];
  for (let i = bits - 1; i >= 0; i -= 1) {
    const idx = bits - 1 - i;
    cells.push(
      <Bit
        key={i}
        x={Number(x) + idx * (size + gap)}
        y={y}
        size={size}
        on={Boolean((value >> i) & 1)}
        dim={upto !== undefined && idx >= upto}
        label={labels ? String(2 ** i) : undefined}
      />,
    );
  }
  return <g>{cells}</g>;
}

/** A colour swatch with its value underneath. */
export function Swatch({ x, y, w = 16, h = 16, fill, label, sub, lit }) {
  const [X, Y, W, H] = [x, y, w, h].map(Number);
  return (
    <g className={`xp-swatch ${lit ? 'is-lit' : ''}`}>
      <rect x={X} y={Y} width={W} height={H} rx="2" fill={fill} />
      {label && <text className="xp-swatch-label" x={X + W / 2} y={Y + H + 4.6}>{label}</text>}
      {sub && <text className="xp-swatch-sub" x={X + W / 2} y={Y + H + 8.8}>{sub}</text>}
    </g>
  );
}

/** A horizontal level meter, for one colour channel. */
export function Channel({ x, y, w = 40, value, tone, label }) {
  const [X, Y, W] = [x, y, w].map(Number);
  return (
    <g className={`xp-chan is-${tone}`}>
      <text className="xp-chan-label" x={X - 2} y={Y + 3.4}>{label}</text>
      <rect className="xp-chan-track" x={X} y={Y} width={W} height={4.6} rx="2.3" />
      <rect className="xp-chan-fill" x={X} y={Y} width={(W * value) / 255} height={4.6} rx="2.3" />
      <text className="xp-chan-value" x={X + W + 2} y={Y + 3.4}>{value}</text>
    </g>
  );
}

/** A packet travelling from one point to another, on a loop. */
export function Packet({ from, to, delay = 0, tone = 'mint', label }) {
  return (
    <g className={`xp-packet is-${tone}`} style={{ animationDelay: `${delay}s` }}>
      <rect x="-2.2" y="-2.2" width="4.4" height="4.4" rx="1">
        <animate
          attributeName="x"
          values={`${from[0] - 2.2};${to[0] - 2.2}`}
          dur="1.6s"
          begin={`${delay}s`}
          repeatCount="indefinite"
        />
        <animate
          attributeName="y"
          values={`${from[1] - 2.2};${to[1] - 2.2}`}
          dur="1.6s"
          begin={`${delay}s`}
          repeatCount="indefinite"
        />
      </rect>
      {label && (
        <text x={(from[0] + to[0]) / 2} y={(from[1] + to[1]) / 2 - 4} className="xp-packet-label">
          {label}
        </text>
      )}
    </g>
  );
}

/** A straight wire between two boxes, optionally with an arrow head. */
export function Wire({ from, to, arrow, dashed, tone = 'dim' }) {
  return (
    <line
      className={`xp-wire is-${tone} ${dashed ? 'is-dashed' : ''}`}
      x1={from[0]}
      y1={from[1]}
      x2={to[0]}
      y2={to[1]}
      markerEnd={arrow ? 'url(#xp-head)' : undefined}
    />
  );
}

/** The cub, standing on the stage and talking. */
export function Cub({ x, y, scale = 1, talking }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} className={`xp-cub ${talking ? 'is-talking' : ''}`}>
      <ellipse className="xp-cub-shadow" cx="0" cy="11" rx="8" ry="1.6" />
      <circle className="xp-cub-ear" cx="-6" cy="-7.5" r="3.1" />
      <circle className="xp-cub-ear" cx="6" cy="-7.5" r="3.1" />
      <circle className="xp-cub-body" cx="0" cy="0" r="10" />
      <circle className="xp-cub-eye" cx="-3.4" cy="-1.6" r="1.25" />
      <circle className="xp-cub-eye" cx="3.4" cy="-1.6" r="1.25" />
      <ellipse className="xp-cub-snout" cx="0" cy="3.2" rx="4.4" ry="3.1" />
      <circle className="xp-cub-nose" cx="0" cy="1.9" r="1.05" />
      <path className="xp-cub-mouth" d="M-2,3.9 q2,2 4,0" />
    </g>
  );
}

/** A caption pinned inside the stage, above the subtitle strip. */
export function Title({ x, y, text, sub }) {
  return (
    <g className="xp-title">
      <text x={x} y={y}>{text}</text>
      {sub && <text className="xp-title-sub" x={x} y={Number(y) + 6.4}>{sub}</text>}
    </g>
  );
}

/** A table, for the database explainer. */
export function Table({ x, y, name, cols, rows, highlight, colWidth = 22 }) {
  const [X, Y] = [x, y].map(Number);
  const w = cols.length * colWidth;
  return (
    <g className="xp-table">
      <rect className="xp-table-frame" x={X} y={Y} width={w} height={8 + rows.length * 7} rx="1.6" />
      <text className="xp-table-name" x={X + 2} y={Y - 2}>{name}</text>
      <rect className="xp-table-head" x={X} y={Y} width={w} height={8} />
      {cols.map((c, i) => (
        <text key={c} className="xp-table-col" x={X + i * colWidth + 2} y={Y + 5.4}>{c}</text>
      ))}
      {rows.map((r, ri) => (
        <g key={ri} className={highlight === ri ? 'is-hit' : ''}>
          {highlight === ri && (
            <rect className="xp-table-hit" x={X} y={Y + 8 + ri * 7} width={w} height={7} />
          )}
          {r.map((cell, ci) => (
            <text key={ci} className="xp-table-cell" x={X + ci * colWidth + 2} y={Y + 8 + ri * 7 + 5}>
              {cell}
            </text>
          ))}
        </g>
      ))}
    </g>
  );
}

/** The arrow head every Wire shares. */
export function ExplainerDefs() {
  return (
    <>
      <marker id="xp-head" markerWidth="5" markerHeight="5" refX="4.2" refY="2.5" orient="auto">
        <path d="M0,0 L5,2.5 L0,5 Z" className="xp-arrow-head" />
      </marker>
      <linearGradient id="xp-spectrum" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#ff4d4d" />
        <stop offset="33%" stopColor="#4dff88" />
        <stop offset="66%" stopColor="#4d9dff" />
        <stop offset="100%" stopColor="#ff4de0" />
      </linearGradient>
    </>
  );
}
